import {inject, Injectable} from "@angular/core";
import {Environment} from "../../enums";
import {EnvironmentDetectionService} from "./environment-detection.service";

@Injectable({
  providedIn: "root"
})
export class FeatureTogglePermissionsService {
  private readonly envDetectionService = inject(EnvironmentDetectionService);

  /**
   * Determines if the current application environment can modify a specific target data environment
   *
   * Permission Matrix:
   * - INTEGRATION can only modify Integration
   * - STAGING can modify Integration and Staging
   * - PRODUCTION can modify all (Integration, Staging, Production)
   *
   * This is also handled on the backend for security, this is just for UI/UX purposes
   *
   * @param targetDataEnv - The environment being viewed/modified (Integration/Staging/Production)
   * @returns boolean - true if modifications are allowed
   */
  public canModifyEnvironment(targetDataEnv: Environment): boolean {
    const currentAppEnv = this.envDetectionService.getCurrentAppEnvironment();

    // INTEGRATION can only modify Integration
    if (currentAppEnv === Environment.INTEGRATION) {
      return targetDataEnv === Environment.INTEGRATION;
    }

    // STAGING can modify Integration and Staging
    if (currentAppEnv === Environment.STAGING) {
      return targetDataEnv === Environment.INTEGRATION || targetDataEnv === Environment.STAGING;
    }

    // PRODUCTION can modify all three
    if (currentAppEnv === Environment.PRODUCTION) {
      return true;
    }

    // Default to deny
    return false;
  }

  /**
   * Get a restriction message text
   */
  public getRestrictionMessage(targetDataEnv: Environment): string {
    if (this.canModifyEnvironment(targetDataEnv)) {
      return "";
    }

    const currentLabel = this.envDetectionService.getCurrentEnvironmentLabel();

    return `Read-only access from current environment (${currentLabel})`;
  }
}
